import { create } from 'zustand'
import {
  getPrepUsageFlags,
  markPrepUsed,
  undoPrepUsed,
  type MarkPrepUsedResponse,
  type PrepUsageFlag,
  type UndoPrepUsedResponse,
} from '../api/prep'

type PrepUsageState = {
  usageByPrepId: Map<number, PrepUsageFlag>
  usageDate: string | null
  loading: boolean
  error: string | null
  hydrateFlags: (flags: PrepUsageFlag[], date?: string | null) => void
  setUsageFlag: (prepId: number, flag: PrepUsageFlag | null, date?: string | null) => void
  fetchUsageFlags: (stationId: number, prepIds: number[], date?: string) => Promise<void>
  markUsed: (stationId: number, prepId: number, date?: string) => Promise<MarkPrepUsedResponse | null>
  undoUsed: (
    stationId: number,
    prepId: number,
    date?: string,
    restore?: { myPrep: boolean; position: number },
  ) => Promise<UndoPrepUsedResponse | null>
}

const usePrepUsageStore = create<PrepUsageState>((set, get) => ({
  usageByPrepId: new Map<number, PrepUsageFlag>(),
  usageDate: null,
  loading: false,
  error: null,
  hydrateFlags: (flags, date) => {
    set((state) => {
      const next = new Map(state.usageByPrepId)
      flags.forEach((flag) => {
        if (flag && Number.isFinite(flag.prep_item_id) && flag.prep_item_id > 0) {
          next.set(flag.prep_item_id, flag)
        }
      })
      return {
        ...state,
        usageByPrepId: next,
        usageDate: typeof date === 'string' && date ? date : state.usageDate,
        loading: false,
        error: null,
      }
    })
  },
  setUsageFlag: (prepId, flag, date) => {
    if (!Number.isFinite(prepId) || prepId <= 0) {
      return
    }
    set((state) => {
      const next = new Map(state.usageByPrepId)
      if (flag) {
        next.set(prepId, flag)
      } else {
        next.delete(prepId)
      }
      return {
        ...state,
        usageByPrepId: next,
        usageDate: typeof date === 'string' && date ? date : state.usageDate,
      }
    })
  },
  fetchUsageFlags: async (stationId, prepIds, date) => {
    if (!stationId || prepIds.length === 0) {
      return
    }
    set((state) => ({ ...state, loading: true, error: null }))
    try {
      const response = await getPrepUsageFlags(stationId, prepIds, date)
      set((state) => {
        const next = new Map(state.usageByPrepId)
        response.flags.forEach((flag) => {
          next.set(flag.prep_item_id, flag)
        })
        return {
          ...state,
          usageByPrepId: next,
          usageDate: response.date ?? state.usageDate,
          loading: false,
          error: null,
        }
      })
    } catch (error) {
      set((state) => ({ ...state, loading: false, error: error instanceof Error ? error.message : 'Failed to load usage' }))
      throw error
    }
  },
  markUsed: async (stationId, prepId, date) => {
    if (!stationId || !prepId) {
      return null
    }
    const response = await markPrepUsed(stationId, prepId, date)
    const flag = response.flag
    set((state) => {
      const next = new Map(state.usageByPrepId)
      next.set(flag.prep_item_id, flag)
      return {
        ...state,
        usageByPrepId: next,
        usageDate: response.date ?? state.usageDate,
      }
    })
    return response
  },
  undoUsed: async (stationId, prepId, date, restore) => {
    if (!stationId || !prepId) {
      return null
    }
    const response = await undoPrepUsed(stationId, prepId, date, restore)
    if (!response.undone) {
      return response
    }
    set((state) => {
      const next = new Map(state.usageByPrepId)
      if (response.flag?.used) {
        next.set(prepId, response.flag)
      } else {
        next.delete(prepId)
      }
      return {
        ...state,
        usageByPrepId: next,
        usageDate: response.date ?? state.usageDate,
      }
    })
    return response
  },
}))

export default usePrepUsageStore
